<template>
	<q-dialog v-model="userStore.openProfileDialog">
		<div class="profile-terminal">
			<div class="profile-frame">
				<div class="profile-term-header">
					<span>OPERATOR PROFILE // EDIT MODE</span>
					<span class="close-btn" @click="userStore.toggleProfile()">&times;</span>
				</div>

				<q-scroll-area style="height: 40vh;">
					<div class="profile-body">
						<!-- Avatar + Identity -->
						<div class="identity-row">
							<div class="avatar-section">
								<img
									:src="getUserPhoto()"
									class="avatar-img"
									@click="triggerAvatarUpload"
								/>
								<input
									ref="avatarInput"
									type="file"
									accept="image/jpeg,image/png,image/webp"
									style="display: none"
									@change="handleAvatarUpload"
								/>
								<div v-if="uploadingAvatar" class="avatar-status uploading">UPLOADING...</div>
								<div v-if="avatarError" class="avatar-status error">{{ avatarError }}</div>
								<div v-else class="avatar-status">CLICK TO CHANGE</div>
							</div>
							<div class="identity-fields">
								<div class="field-row">
									<span class="field-label">NAME</span>
									<span class="field-value">{{ userStore.firstName }}</span>
									<span v-if="userStore.isAdmin" class="admin-badge">ADMIN</span>
								</div>
								<div class="field-row">
									<span class="field-label">ALIAS</span>
									<input
										v-model="editableAlias"
										class="term-input"
										placeholder="Enter alias..."
										@blur="validateAlias"
									/>
								</div>
								<div v-if="aliasError" class="field-error">{{ aliasError }}</div>
								<div class="field-row">
									<span class="field-label">E-MAIL</span>
									<span class="field-value dim">{{ userStore.email }}</span>
								</div>
							</div>
						</div>

						<!-- Tags -->
						<div class="section">
							<div class="section-label">PROFILE TAGS</div>
							<div class="tags-row">
								<span
									v-for="tag in editableTags"
									:key="tag"
									class="tag-item"
									:class="{ confirming: confirmingTag === tag }"
									@click="onTagClick(tag)"
								>
									{{ confirmingTag === tag ? 'REMOVE?' : getTagLabel(tag) }}
								</span>
								<button class="tag-add" @click="showTagPicker = true">+</button>
							</div>
						</div>

						<!-- Text size -->
						<div class="section">
							<div class="section-label">TEXT SIZE</div>
							<div class="fontscale-row">
								<input
									type="range"
									min="0"
									max="30"
									step="1"
									v-model.number="editableFontScale"
									class="fontscale-slider"
									@input="onFontScaleInput"
								/>
								<span class="fontscale-val">{{ editableFontScale > 0 ? '+' + editableFontScale + '%' : 'DEFAULT' }}</span>
							</div>
							<div class="fontscale-sample">The quick brown fox jumps over 5 lazy dogs.</div>
						</div>

						<!-- Tag picker dialog -->
						<q-dialog v-model="showTagPicker">
							<div class="tag-picker-terminal">
								<div class="picker-header">SELECT TAGS</div>
								<div class="picker-list">
									<div
										v-for="tag in availableTags"
										:key="tag.value"
										class="picker-item"
										:class="{ active: editableTags.includes(tag.value) }"
										@click="toggleTag(tag.value)"
									>
										<span class="picker-check">{{ editableTags.includes(tag.value) ? '\u2714' : '\u25CB' }}</span>
										{{ tag.label }}
									</div>
								</div>
								<ScifiButton text="DONE" buttonType="positive" minWidth="100%" @click="showTagPicker = false" />
							</div>
						</q-dialog>

						<!-- API Token -->
						<div class="section">
							<div class="section-label clickable" @click="showToken = !showToken">
								{{ showToken ? '\u25BC' : '\u25B6' }} API ACCESS TOKEN
							</div>
							<div v-if="showToken" class="token-section">
								<div v-if="!userStore.apiToken" class="token-empty">
									<div class="dim">Generate a token for external API access.</div>
									<ScifiButton text="GENERATE TOKEN" buttonType="positive" @click="handleGenerateToken" />
								</div>
								<div v-else-if="!userStore.apiToken.active" class="token-revoked">
									TOKEN REVOKED — CONTACT ADMIN
								</div>
								<div v-else>
									<div class="token-display">
										<code class="token-code">{{ displayToken }}</code>
										<div class="token-actions">
											<button class="term-btn-sm" @click="copyToken" :title="copied ? 'Copied!' : 'Copy'">
												{{ copied ? '\u2714' : '\u2398' }}
											</button>
											<button class="term-btn-sm" @click="showFullToken = !showFullToken">
												{{ showFullToken ? '\u25CF' : '\u25CB' }}
											</button>
										</div>
									</div>
									<div class="token-meta">
										<span>CREATED: {{ formatDate(userStore.apiToken.createdAt) }}</span>
										<span>EXPIRES: {{ userStore.apiToken.expiresAt ? formatDate(userStore.apiToken.expiresAt) : 'NEVER' }}</span>
									</div>
									<ScifiButton text="REGENERATE" buttonType="warning" @click="handleInvalidateToken" />
								</div>
							</div>
						</div>
					</div>
				</q-scroll-area>

				<!-- Actions -->
				<div v-if="confirmDelete" class="delete-warning">
					&#9888; This permanently removes your characters, games, and all data related to Cybercore. Anyone you have shared characters or other information with keeps the copies they have already made. This cannot be undone.
				</div>
				<div class="profile-actions">
					<ScifiButton text="SAVE PROFILE" buttonType="positive" :loading="saving" loadingText="SAVING..." @click="updateProfile" />
					<ScifiButton :text="confirmDelete ? 'CONFIRM DELETE' : 'DELETE PROFILE'" buttonType="negative" @click="confirmDelete ? deleteProfile() : (confirmDelete = true)" />
				</div>
			</div>
		</div>
	</q-dialog>
</template>

<script setup lang="ts">
	import { ref, computed, watch } from 'vue';
	import { useUserStore } from '../stores/user';
	import { maskToken } from '~/utils/apiToken';
	import { PROFILE_TAGS, type ProfileTag, THEMES, type ThemeId } from '~/models/user';
	import { getFunctions, httpsCallable } from 'firebase/functions';
	import type { Timestamp } from 'firebase/firestore';
	import { ref as storageRef, uploadBytes } from 'firebase/storage';
	import type { FirebaseStorage } from 'firebase/storage';

	const userStore = useUserStore();
	const showFullToken = ref(false);
	const showToken = ref(false);
	const copied = ref(false);
	const confirmDelete = ref(false);
	const saving = ref(false);
	const editableAlias = ref(userStore.profile?.alias || '');
	const editableTags = ref<ProfileTag[]>([...(userStore.profile?.tags || ['player'])]);
	const editableTheme = ref<ThemeId | ''>(userStore.profile?.theme || '');
	const availableThemes = THEMES;
	const editableFontScale = ref<number>(userStore.profile?.fontScale ?? 0);
	const aliasError = ref('');
	const showTagPicker = ref(false);
	const confirmingTag = ref<ProfileTag | null>(null);
	let confirmTimeout: ReturnType<typeof setTimeout> | null = null;
	const availableTags = PROFILE_TAGS;
	const avatarInput = ref<HTMLInputElement | null>(null);
	const uploadingAvatar = ref(false);
	const avatarError = ref('');

	const getTagLabel = (tag: ProfileTag): string => {
		return availableTags.find(t => t.value === tag)?.label || tag;
	};

	const removeTag = (tag: ProfileTag) => {
		editableTags.value = editableTags.value.filter(t => t !== tag);
	};

	const onTagClick = (tag: ProfileTag) => {
		if (confirmingTag.value === tag) {
			removeTag(tag);
			confirmingTag.value = null;
			if (confirmTimeout) clearTimeout(confirmTimeout);
		} else {
			confirmingTag.value = tag;
			if (confirmTimeout) clearTimeout(confirmTimeout);
			confirmTimeout = setTimeout(() => { confirmingTag.value = null; }, 2000);
		}
	};

	watch(() => userStore.profile?.alias, (v) => { editableAlias.value = v || ''; });
	watch(() => userStore.profile?.tags, (v) => { editableTags.value = [...(v || ['player'])]; });
	watch(() => userStore.profile?.theme, (v) => { editableTheme.value = v || ''; });
	watch(() => userStore.profile?.fontScale, (v) => { editableFontScale.value = v ?? 0; });

	// Live-preview the text size while dragging: push the value onto the
	// reactive profile so the app.vue watcher applies --cc-font-scale at once.
	// Persisted to Firestore on Save (updateProfile).
	const onFontScaleInput = () => {
		if (userStore.profile) userStore.profile.fontScale = editableFontScale.value;
	};

	const toggleTag = (tag: ProfileTag) => {
		const idx = editableTags.value.indexOf(tag);
		if (idx === -1) editableTags.value.push(tag);
		else editableTags.value.splice(idx, 1);
	};

	const validateAlias = async () => {
		aliasError.value = '';
		const alias = editableAlias.value.trim();
		if (!alias || alias === userStore.profile?.alias) return;
		try {
			const functions = getFunctions();
			const checkAlias = httpsCallable<{ alias: string }, { available: boolean }>(functions, 'checkAliasAvailable');
			const result = await checkAlias({ alias });
			if (!result.data.available) aliasError.value = 'This alias is already taken';
		} catch (error) {
			aliasError.value = 'Could not validate alias';
		}
	};

	const MAX_AVATAR_SIZE = 1_000_000;
	const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

	const triggerAvatarUpload = () => { avatarInput.value?.click(); };

	const handleAvatarUpload = async (event: Event) => {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;
		avatarError.value = '';
		if (!ALLOWED_TYPES.includes(file.type)) { avatarError.value = 'JPG, PNG, or WebP only'; input.value = ''; return; }
		if (file.size > MAX_AVATAR_SIZE) { avatarError.value = 'Max 1 MB'; input.value = ''; return; }
		const uid = userStore.profile?.uid;
		if (!uid) return;
		const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
		const { $storage } = useNuxtApp();
		const avatarRef = storageRef($storage as FirebaseStorage, `public/${uid}/avatar.${ext}`);
		uploadingAvatar.value = true;
		try {
			const snapshot = await uploadBytes(avatarRef, file, { contentType: file.type });
			// Deterministic public URL — /public/{uid}/* is public read so
			// this works for any viewer (including Foundry player browsers)
			// and never rotates when the avatar is replaced.
			const downloadURL = `https://firebasestorage.googleapis.com/v0/b/${snapshot.ref.bucket}/o/${encodeURIComponent(snapshot.ref.fullPath)}?alt=media`;
			if (userStore.profile) userStore.profile.photoURL = downloadURL;
			const { $firestore } = useNuxtApp();
			const { doc, setDoc } = await import('firebase/firestore');
			await setDoc(doc($firestore as any, '/users', uid), userStore.profile);
		} catch (error) {
			avatarError.value = 'Upload failed';
		} finally {
			uploadingAvatar.value = false;
			input.value = '';
		}
	};

	const getUserPhoto = () => userStore.profile?.photoURL || '/svg/cybercore_trans.svg';

	const displayToken = computed(() => {
		if (!userStore.apiToken) return '';
		return showFullToken.value ? userStore.apiToken.token : maskToken(userStore.apiToken.token);
	});

	const formatDate = (timestamp: Timestamp | null) => {
		if (!timestamp) return 'N/A';
		return timestamp.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	};

	const copyToken = async () => {
		if (!userStore.apiToken) return;
		try {
			await navigator.clipboard.writeText(userStore.apiToken.token);
			copied.value = true;
			setTimeout(() => { copied.value = false; }, 2000);
		} catch (error) { console.error('Copy failed:', error); }
	};

	const handleGenerateToken = async () => { await userStore.generateApiToken(); };
	const handleInvalidateToken = async () => { await userStore.refreshApiToken(); showFullToken.value = false; };

	const updateProfile = async () => {
		if (aliasError.value || saving.value) return;
		saving.value = true;
		try {
			if (userStore.profile) {
				await validateAlias();
				if (aliasError.value) return;
				userStore.profile.alias = editableAlias.value.trim();
				userStore.profile.tags = [...editableTags.value];
				userStore.profile.fontScale = editableFontScale.value;
				const { $firestore } = useNuxtApp();
				const { doc, setDoc } = await import('firebase/firestore');
				try {
					await setDoc(doc($firestore as any, '/users', userStore.profile.uid), userStore.profile);
				} catch (error) {
					console.error('Error updating profile:', error);
				}
			}
			userStore.toggleProfile();
		} finally {
			saving.value = false;
		}
	};

	const deleteProfile = async () => {
		userStore.toggleProfile();
		await userStore.deleteAccount(); // cascade: profile, characters, games, invites, auth
		navigateTo('/');
	};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');

.profile-terminal {
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	color: #00ffea;
	width: 520px;
}

.profile-frame {
	background: #050a0e;
	border: 2px solid #00ffea;
	border-radius: 8px;
	box-shadow: 0 0 20px #00ffea30, inset 0 0 40px #00000080;
	overflow: hidden;
}

.profile-term-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 16px;
	background: #0a1418;
	border-bottom: 1px solid #00ffea40;
	font-size: 0.75em;
	letter-spacing: 2px;
	color: #ff6600;
}

.close-btn {
	cursor: pointer;
	font-size: 1.6em;
	color: #00ffea60;
	transition: color 0.15s;
}

.close-btn:hover {
	color: #ff4444;
}

.profile-body {
	padding: 20px;
}

.identity-row {
	display: flex;
	gap: 20px;
	margin-bottom: 20px;
	padding-bottom: 16px;
	border-bottom: 1px solid #00ffea15;
}

.avatar-section {
	text-align: center;
	flex-shrink: 0;
}

.avatar-img {
	width: 96px;
	height: 96px;
	border-radius: 50%;
	border: 2px solid #00ffea60;
	object-fit: contain;
	cursor: pointer;
	transition: border-color 0.2s, box-shadow 0.2s;
}

.avatar-img:hover {
	border-color: #00ffea;
	box-shadow: 0 0 16px #00ffea40;
}

.avatar-status {
	font-size: 0.6em;
	color: #00ffea45;
	margin-top: 4px;
	letter-spacing: 1px;
}

.avatar-status.uploading { color: #00ffea; }
.avatar-status.error { color: #ff4444; }

.identity-fields {
	flex: 1;
}

.field-row {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 8px;
}

.field-label {
	font-size: 0.7em;
	color: #00ffea70;
	letter-spacing: 2px;
	width: 60px;
	flex-shrink: 0;
}

.field-value {
	font-size: 0.9em;
}

.field-value.dim {
	color: #00ffea60;
	font-size: 0.8em;
}

.admin-badge {
	font-size: 0.65em;
	letter-spacing: 2px;
	color: #ff6600;
	border: 1px solid #ff6600;
	padding: 1px 8px;
	border-radius: 2px;
	background: #ff660015;
	margin-left: auto;
}

.field-error {
	color: #ff4444;
	font-size: 0.7em;
	margin-top: -4px;
	margin-bottom: 8px;
	padding-left: 72px;
}

.term-input {
	background: #0a1418;
	border: 1px solid #00ffea30;
	color: #00ffea;
	font-family: inherit;
	font-size: 0.9em;
	padding: 4px 8px;
	border-radius: 2px;
	flex: 1;
}

.term-input:focus {
	outline: none;
	border-color: #00ffea;
}

.term-input::placeholder {
	color: #00ffea25;
}

.section {
	margin-bottom: 16px;
}

.section-label {
	font-size: 0.7em;
	color: #00ffea70;
	letter-spacing: 2px;
	margin-bottom: 8px;
	border-bottom: 1px solid #00ffea10;
	padding-bottom: 4px;
}

.section-label.clickable {
	cursor: pointer;
}

.fontscale-row {
	display: flex;
	align-items: center;
	gap: 12px;
}

.fontscale-slider {
	flex: 1;
	appearance: none;
	-webkit-appearance: none;
	height: 4px;
	border-radius: 2px;
	background: #00ffea30;
	outline: none;
	cursor: pointer;
}

.fontscale-slider::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	width: 16px;
	height: 16px;
	border-radius: 50%;
	background: #00ffea;
	border: none;
	cursor: pointer;
	box-shadow: 0 0 6px #00ffea80;
}

.fontscale-slider::-moz-range-thumb {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	background: #00ffea;
	border: none;
	cursor: pointer;
}

.fontscale-val {
	min-width: 68px;
	text-align: right;
	color: #00ffea;
	font-size: 0.8em;
	letter-spacing: 1px;
}

.fontscale-sample {
	margin-top: 8px;
	color: #9fbcc2;
	font-size: var(--fs-small);
}

.section-label.clickable:hover {
	color: #00ffea;
}

.tags-row {
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
	align-items: center;
}

.tag-item {
	border: 1px solid #51ff0060;
	color: #51ff00;
	padding: 2px 10px;
	border-radius: 2px;
	font-size: 0.75em;
	letter-spacing: 1px;
	cursor: pointer;
	transition: all 0.15s;
	text-transform: uppercase;
}

.tag-item:hover {
	background: #51ff0015;
}

.tag-item.confirming {
	border-color: #ff4444;
	color: #ff4444;
	background: #ff444415;
}

.tag-add {
	background: none;
	border: 1px solid #ff660060;
	color: #ff6600;
	width: 24px;
	height: 24px;
	border-radius: 2px;
	cursor: pointer;
	font-family: inherit;
	font-size: 1em;
}

.tag-add:hover {
	background: #ff660020;
}

.theme-select {
	width: 100%;
	background: #0a1418;
	border: 1px solid #00ffea30;
	color: #00ffea;
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	font-size: 0.85em;
	padding: 6px 10px;
	border-radius: 2px;
}

.theme-select:focus {
	outline: none;
	border-color: #00ffea;
}

.theme-select option {
	background: #050a0e;
	color: #00ffea;
}

.theme-preview-hint {
	font-size: 0.65em;
	color: #00ffea50;
	margin-top: 4px;
	letter-spacing: 1px;
}

.theme-preview-hint a {
	color: #51ff00;
	text-decoration: underline;
}

.tag-picker-terminal {
	background: #050a0e;
	border: 2px solid #ff6600;
	border-radius: 8px;
	padding: 16px;
	min-width: 280px;
	font-family: 'Share Tech Mono', 'Courier New', monospace;
	color: #00ffea;
}

.picker-header {
	font-size: 0.75em;
	color: #ff6600;
	letter-spacing: 2px;
	margin-bottom: 12px;
}

.picker-list {
	max-height: 300px;
	overflow-y: auto;
}

.picker-item {
	padding: 6px 8px;
	cursor: pointer;
	font-size: 0.85em;
	border-bottom: 1px solid #00ffea08;
	transition: background 0.1s;
}

.picker-item:hover {
	background: #00ffea10;
}

.picker-item.active {
	background: #51ff0010;
}

.picker-check {
	margin-right: 8px;
	color: #51ff00;
}

.token-section {
	margin-top: 8px;
}

.token-empty .dim {
	color: #00ffea60;
	font-size: 0.8em;
	margin-bottom: 8px;
}

.token-revoked {
	color: #ff4444;
	font-size: 0.8em;
	letter-spacing: 1px;
}

.token-display {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}

.token-code {
	background: #0a1418;
	border: 1px solid #00ffea20;
	color: #00ffea80;
	padding: 4px 8px;
	border-radius: 2px;
	font-size: 0.75em;
	flex: 1;
	word-break: break-all;
}

.token-actions {
	display: flex;
	gap: 4px;
}

.token-meta {
	display: flex;
	justify-content: space-between;
	font-size: 0.65em;
	color: #00ffea60;
	margin-bottom: 8px;
}

.term-btn {
	background: none;
	border: 1px solid currentColor;
	color: #00ffea;
	font-family: inherit;
	font-size: 0.8em;
	padding: 6px 16px;
	cursor: pointer;
	border-radius: 2px;
	letter-spacing: 1px;
	transition: all 0.15s;
}

.term-btn:hover {
	background: currentColor;
	color: #000;
}

.term-btn.green { color: #51ff00; }
.term-btn.red { color: #ff4444; }
.term-btn.orange { color: #ff6600; }
.term-btn.full { width: 100%; margin-top: 12px; }

.term-btn-sm {
	background: none;
	border: 1px solid #00ffea30;
	color: #00ffea60;
	font-size: 0.8em;
	padding: 2px 6px;
	cursor: pointer;
	border-radius: 2px;
}

.term-btn-sm:hover {
	color: #00ffea;
	border-color: #00ffea;
}

.profile-actions {
	display: flex;
	justify-content: space-between;
	padding: 12px 20px;
	border-top: 1px solid #00ffea20;
	background: #0a1418;
}

.delete-warning {
	color: #ff8a8a;
	border: 1px solid #ff3b3080;
	background: #2a0808;
	border-radius: 4px;
	padding: 10px 12px;
	font-size: 0.82em;
	line-height: 1.45;
	margin-bottom: 10px;
}

</style>
